import { Component } from '@theme/component';

/**
 * @typedef {object} StockistDirectoryRefs
 * @property {HTMLInputElement} input
 * @property {HTMLButtonElement} [reset]
 * @property {HTMLElement} [emptyMessage]
 * @property {HTMLElement[]} [categories]
 * @property {HTMLElement[]} [items]
 * @property {HTMLAnchorElement[]} [navLinks]
 */

/**
 * Stockist directory with client-side search and a scroll-spy nav.
 * Filters stockist cards in place by address text (matching the old theme's
 * behavior), hides categories left empty, and shows an empty message when
 * nothing matches. While scrolling, the nav link of the category currently
 * under the header gets `aria-current="true"` (styled black).
 *
 * @extends {Component<StockistDirectoryRefs>}
 */
class StockistDirectory extends Component {
  requiredRefs = ['input'];

  /** @type {IntersectionObserver | undefined} */
  #spy;

  connectedCallback() {
    super.connectedCallback();
    this.#setupScrollSpy();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#spy?.disconnect();
  }

  /**
   * Marks the category whose top edge is closest above the header's bottom
   * line as current. Runs on intersection changes; the ranking is done over
   * every visible category so the answer is stable regardless of the order
   * IntersectionObserver reports entries in.
   */
  #setupScrollSpy() {
    const categories = this.refs.categories ?? [];
    const links = this.refs.navLinks ?? [];
    if (categories.length === 0 || links.length === 0 || !('IntersectionObserver' in window)) return;

    /** @type {HTMLElement|null} category the user explicitly navigated to; wins until the scroll settles */
    let pinned = null;
    let pinTimer = 0;

    const markCurrent = (/** @type {HTMLElement|null} */ current) => {
      for (const link of links) {
        const isCurrent = current !== null && link.hash === `#${current.id}`;
        if (isCurrent) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      }
    };

    const update = () => {
      if (pinned) {
        markCurrent(pinned);
        return;
      }
      const first = categories[0];
      // Detection line = where an anchored category lands (its scroll-margin-top),
      // so a clicked region is "current" the moment the jump finishes. Half a pixel
      // of tolerance absorbs sub-pixel rounding of smooth scroll.
      const line = first ? parseFloat(getComputedStyle(first).scrollMarginTop) || 0 : 0;
      let current = null;
      let best = -Infinity;

      for (const category of categories) {
        if (category.hidden) continue;
        const { top, bottom } = category.getBoundingClientRect();
        // Candidate: category has started (top at/above the line) and hasn't fully passed.
        if (top <= line + 0.5 && bottom > line && top > best) {
          best = top;
          current = category;
        }
      }

      // Before the first category has reached the line, keep the first visible one active.
      if (!current) current = categories.find((c) => !c.hidden) ?? null;
      markCurrent(current);
    };

    // A click on a region is an explicit choice: highlight it immediately and hold it
    // through the smooth scroll (intermediate frames would otherwise flash the regions
    // being scrolled past). The pin is released once the scroll has settled — every
    // scroll event pushes the release back — with a longer fallback for the case where
    // the target is already in view and no scroll happens at all.
    const SCROLL_SETTLE_MS = 150;
    const NO_SCROLL_FALLBACK_MS = 800;
    const releasePin = (delay) => {
      clearTimeout(pinTimer);
      pinTimer = window.setTimeout(() => {
        pinned = null;
        update();
      }, delay);
    };
    for (const link of links) {
      link.addEventListener('click', () => {
        pinned = categories.find((c) => `#${c.id}` === link.hash) ?? null;
        markCurrent(pinned);
        releasePin(NO_SCROLL_FALLBACK_MS);
      });
    }
    window.addEventListener('scroll', () => { if (pinned) releasePin(SCROLL_SETTLE_MS); }, { passive: true });

    this.#spy = new IntersectionObserver(update, {
      // Fine-grained thresholds so tall categories keep firing while they scroll through.
      threshold: Array.from({ length: 21 }, (_, i) => i / 20),
    });
    for (const category of categories) this.#spy.observe(category);
    update();
  }

  /** @param {SubmitEvent} event */
  handleSubmit(event) {
    event.preventDefault();
    this.#filter();
  }

  handleInput() {
    this.#filter();
  }

  /** @param {MouseEvent} event */
  handleReset(event) {
    event.preventDefault();
    this.refs.input.value = '';
    this.#filter();
    this.refs.input.focus();
  }

  #filter() {
    const query = this.refs.input.value.trim().toLowerCase();
    const items = this.refs.items ?? [];
    let visibleTotal = 0;

    for (const item of items) {
      const matches = query === '' || (item.dataset.address ?? '').includes(query);
      item.hidden = !matches;
      if (matches) visibleTotal += 1;
    }

    for (const category of this.refs.categories ?? []) {
      const hasVisible = category.querySelector('[data-stockist-item]:not([hidden])') !== null;
      category.hidden = !hasVisible;
    }

    if (this.refs.reset) this.refs.reset.hidden = query === '';
    if (this.refs.emptyMessage) this.refs.emptyMessage.hidden = visibleTotal > 0 || query === '';
    this.toggleAttribute('data-filtering', query !== '');
  }
}

customElements.define('stockist-directory', StockistDirectory);
